import * as React from 'react';
import { LanguageContext } from '../../utilities/LocalizationModule';
import {
    Label,
    CommandBar,
    ICommandBarItemProps,
    Stack,
    FontSizes,
    FontWeights,
    ILabelStyles,
    ScrollablePane,
    ScrollbarVisibility,
    mergeStyles,
    MessageBar,
    MessageBarType,
    SearchBox,
    Spinner,
    SpinnerSize,
} from '@fluentui/react';
import { RootState } from '../../redux';
import { connect, ConnectedProps } from 'react-redux';
import AddSubscriptionPanel from '../panels/AddSubscriptionPanel';
import { assignAllUsersToSubscription, cancelSubscription, getMultiTenantSubscriptions, getPaymentStatus, getTenantSubscriptions, getTenantSubscriptionsPayments, listAvailablePaymentMethods, revokeAllUsersFromSubscription, updateSubscriptionStatus } from '../../utilities/helpers/ApiHelper';
import SubscriptionsList from '../SubscriptionsList';
import SubscriptionsPaymentsList from '../SubscriptionsPaymentsList';
import SelectTenantPanel from '../panels/SelectTenantPanel';
import { AssignSubscriptionRequest, ITenant, ITenantUser, MultiTenantSubscriptionRequest, PaymentStatusResponse, SubscriptionStatusUpdateRequest } from '../../data-structures/interfaces';
import { labelHeadingStyle } from '../../styles/PanelStyle';
import { SubscriptionStatusEnum, SubscriptionTierEnum } from '../../data-structures/enums';
import { getUserLicenseType } from '../../utilities/helpers/UserLicenseTypeHelper';
import { updateUser } from '../../redux/modules/user';
import * as UserTableStyle from "../../styles/UserTableStyle"
import SubscriptionsExportPanel from '../panels/SubscriptionsExportPanel';
import XLSX from 'xlsx-js-style';

export const headingStyle: Partial<ILabelStyles> = {
    root: {
        fontWeight: FontWeights.semibold,
        fontSize: FontSizes.size28,
    }
}

export const tableContainerStyle = mergeStyles({
    // height: "calc(100vh - 50px - 52px - 42px - 30px)",
    height: "calc(100vh - 50px - 52px - 42px)",
    position: "relative",
})

const mapDispatchToProps = {
    updateUser
}

const mapStateToProps = (state: RootState) => {
    return {
        tenants: state.user.tenants,
        users: state.user.users,
        isGlobalAdmin: state.user.isGlobalAdmin,
        account: state.user.account
    };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface SubscriptionsProps extends PropsFromRedux {
    transactionId?: string;
    defaultTenant?: ITenant;
};

const Subscriptions: React.FC<SubscriptionsProps> = ({
    tenants,
    transactionId,
    users,
    updateUser,
    defaultTenant,
    isGlobalAdmin,
    account
}: SubscriptionsProps) => {

    const languageStrings = React.useContext(LanguageContext);

    const [isAddSubscriptionPanelOpen, setIsAddSubscriptionPanelOpen] = React.useState(false);
    const [isSelectTenantPanelOpen, setIsSelectTenantPanelOpen] = React.useState(false);
    const [isExportPanelOpen, setIsExportPanelOpen] = React.useState(false);
    const [subscriptions, setSubscriptions] = React.useState<any[]>([]);
    // const [payments, setPayments] = React.useState<any[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [paymentMethods, setPaymentMethods] = React.useState<any[]>([]);
    const [selectedTenant, setSelectedTenant] = React.useState<ITenant>(defaultTenant);
    const [filterValue, setFilterValue] = React.useState<string>();

    const [selectedItems, setSelectedItems] = React.useState<any[]>([]);
    const [isBusy, setIsBusy] = React.useState(false);
    const [isExporting, setIsExporting] = React.useState<boolean>(false);
    const [deselectAllSubscriptions, setDeselectAllSubscriptions] = React.useState(false);

    const [paymentStatus, setPaymentStatus] = React.useState<PaymentStatusResponse>();

    const fetchSubscrptionsAndPayments = async () => {
        // const [subscriptionsRes, paymmentRes] = await Promise.all([
        //     getTenantSubscriptions(selectedTenant.id, languageStrings),
        //     getTenantSubscriptionsPayments(selectedTenant.id)
        // ])

        // setSubscriptions(subscriptionsRes.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()));
        // setPayments(paymmentRes.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()));

        const subscriptionsRes = await getTenantSubscriptions(selectedTenant.id, languageStrings, tenants);
        setSubscriptions(subscriptionsRes.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()));
    }

    const fetchMultiTenantSubscrptions = async () => {
        const request: MultiTenantSubscriptionRequest = {
            tenantIds: tenants.map(x => x.id),
            userProfile: {
                accountId: account.homeAccountId,
                username: account.username
            }
        }

        const multiSubscriptions = await getMultiTenantSubscriptions(request, tenants, languageStrings);

        setSubscriptions(
            multiSubscriptions
                .sort((a, b) => {
                    // Sort by tenantName
                    if (a.tenantName < b.tenantName) return -1;
                    if (a.tenantName > b.tenantName) return 1;

                    // If tenantName is the same, then sort by createdDate
                    return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
                })
        );
    }

    React.useEffect(() => {
        if (Array.isArray(subscriptions) && subscriptions.length > 0 && Array.isArray(selectedItems) && selectedItems.length === 1) {
            const sub = subscriptions.find(x => x.id === selectedItems[0].id);
            if (sub && JSON.stringify(sub) !== JSON.stringify(selectedItems[0])) {
                setSelectedItems([sub]);
            }
        }
    }, [subscriptions, selectedItems]);

    React.useEffect(() => {
        if (tenants?.length === 1) {
            setSelectedTenant(tenants[0])
        }
    }, [tenants]);

    React.useEffect(() => {
        if (transactionId && tenants?.length > 0) {
            (async () => {
                const results = await getPaymentStatus(transactionId);
                const selectedTenant = tenants.find(x => x.id === results.tenantId);
                if (selectedTenant) {
                    setSelectedTenant(selectedTenant);
                }
                setPaymentStatus(results);
            })()
        }
    }, [transactionId, tenants]);

    // React.useEffect(() => {
    //     (async () => {
    //         setPaymentMethods(await listAvailablePaymentMethods());
    //     })();
    // }, []);

    React.useEffect(() => {
        if (selectedTenant) {
            setFilterValue(null);
            setSubscriptions([]);
            fetchSubscrptionsAndPayments();
        } else if (!selectedTenant && tenants?.length > 1 && !transactionId) {
            fetchMultiTenantSubscrptions();
        }
    }, [selectedTenant, transactionId]);

    const commandBarItems = React.useMemo(() => {
        const items: ICommandBarItemProps[] = [];

        // --- Tenant Selection Actions ---
        if (tenants.length > 1) {
            if (selectedTenant) {
                items.push({
                    key: "alltenants",
                    text: `${languageStrings.ShowAll} ${languageStrings.Tenants}`,
                    iconProps: { iconName: "SearchAndApps" },
                    disabled: isBusy,
                    onClick: () => {
                        setFilterValue(null);
                        setSelectedTenant(undefined);
                    }
                });
            }
            items.push({
                key: "tenant",
                text: selectedTenant ? `${languageStrings.Change} ${languageStrings.Tenant}` : languageStrings.SelectTenant,
                iconProps: { iconName: "UserSync" },
                disabled: isBusy,
                onClick: () => setIsSelectTenantPanelOpen(true)
            });
        }

        // --- Subscription Management Actions ---
        if (selectedTenant && isGlobalAdmin) {
            items.push({
                key: "add",
                text: languageStrings.AddSubscription,
                iconProps: { iconName: "Add" },
                disabled: !selectedTenant || isBusy,
                onClick: () => setIsAddSubscriptionPanelOpen(true)
            });
        }

        // --- Actions for a Single Selected Subscription ---
        if (selectedItems.length === 1) {
            const singleItem = selectedItems[0];
            const status = (singleItem.status as string)?.toLowerCase();
            const expiryDate = singleItem.expiryDateRaw ? new Date(singleItem.expiryDateRaw) : null;
            const isExpired = expiryDate && expiryDate < new Date();

            const canAssign = (status === SubscriptionStatusEnum.Active || (status === SubscriptionStatusEnum.Canceled && expiryDate && expiryDate >= new Date()));

            items.push(
                {
                    key: "assignall",
                    text: languageStrings.AssignAllLicenses,
                    iconProps: { iconName: "UserFollowed" },
                    disabled: isBusy || singleItem.availableLicenses === 0 || isExpired || !canAssign,
                    onClick: () => {
                        setIsBusy(true);
                        assignAll(singleItem);
                    }
                },
                {
                    key: "revoke",
                    text: languageStrings.RevokeLicenses,
                    iconProps: { iconName: "UserRemove" },
                    disabled: isBusy || !singleItem.assignedLicenses || singleItem.assignedLicenses === "-",
                    onClick: () => {
                        setIsBusy(true);
                        revokeAll(selectedItems);
                    }
                },
            );
        }

        // --- Actions for One or More Selected Subscriptions ---
        if (selectedItems.length > 0) {
            // --- Suspend/Resume Action ---
            const areAllInvoiceBased = selectedItems.every(item => item.method === "invoice" || item.method === "invoice_static" || item.method === "invoice_dynamic");
            const areAllActive = selectedItems.every(item => (item.status as string)?.toLowerCase() === SubscriptionStatusEnum.Active);
            const areAllSuspended = selectedItems.every(item => (item.status as string)?.toLowerCase() === SubscriptionStatusEnum.Suspended);

            if (isGlobalAdmin && areAllInvoiceBased && (areAllActive || areAllSuspended)) {
                items.push({
                    key: "suspend",
                    text: areAllActive ? languageStrings.Suspend : languageStrings.Resume,
                    iconProps: { iconName: areAllActive ? "CircleStop" : "Play" },
                    disabled: isBusy,
                    onClick: () => {
                        setIsBusy(true);
                        updateStatus(selectedItems, areAllActive ? SubscriptionStatusEnum.Suspended : SubscriptionStatusEnum.Active);
                    }
                });
            }

            // --- Cancel Action ---
            const isCancellationDisallowed = selectedItems.some(item => {
                const status = (item.status as string)?.toLowerCase();

                if (status === SubscriptionStatusEnum.Active && item.tier === SubscriptionTierEnum.Trial && isGlobalAdmin) {
                    return false;
                }

                return status !== SubscriptionStatusEnum.Active
                    || (item.method !== "invoice" && item.method !== "invoice_static" && item.method !== "invoice_dynamic");
            });

            if (!isCancellationDisallowed) {
                items.push({
                    key: "cancel",
                    text: languageStrings.Cancel,
                    iconProps: { iconName: "Cancel" },
                    disabled: isBusy,
                    onClick: () => {
                        setIsBusy(true);
                        cancelSubscriptions(selectedItems);
                    }
                });
            }
        }

        // --- Global Actions ---
        items.push({
            key: "export",
            text: languageStrings.ExportToExcel,
            iconProps: { iconName: "ExcelDocument" },
            onClick: () => { exportFile(); },
            onRenderIcon: isExporting ? () => <Spinner size={SpinnerSize.small} style={{ marginRight: 4, marginLeft: 4 }} /> : undefined
        });

        return items;
    }, [subscriptions, selectedTenant, selectedItems, isBusy, isExporting, tenants, isGlobalAdmin, languageStrings]);

    // const exportFile = () => {
    //     setIsExportPanelOpen(true);
    // };
    const exportFile = React.useCallback(async () => {
        setIsExporting(true);
        await new Promise(r => setTimeout(r, 200));
        const ws = XLSX.utils.json_to_sheet(subscriptions?.map(item => ({
            createdDate: `${new Date(item.createdDate)?.toLocaleDateString()} ${new Date(item.createdDate)?.toLocaleTimeString()}`,
            tenantName: item.tenantName,
            status: item.status,
            interval: item.interval,
            product: item.product,
            description: item.description,
            licenseCount: item.licenseCount?.toString(),
            availableLicenses: item.availableLicenses !== "-" ? item.availableLicenses : "",
            assignedLicenses: item.assignedLicenses !== "-" ? item.assignedLicenses : "",
            expiryDate: item.expiryDateRaw ? `${new Date(item.expiryDateRaw)?.toLocaleDateString()} ${new Date(item.expiryDateRaw)?.toLocaleTimeString()}` : "",
            paymentPlatform: item.paymentPlatform,
            amountString: item.amountString !== "-" ? item.amountString : "",
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, languageStrings.Subscriptions);

        const headerRow = [
            languageStrings.Created,
            languageStrings.Tenant,
            languageStrings.Status,
            languageStrings.Frequency,
            languageStrings.Product,
            languageStrings.Description,
            languageStrings.LicenseCount,
            languageStrings.AvailableLicences,
            languageStrings.AssignedLicences,
            languageStrings.ExpiryDate,
            languageStrings.PaymentPlatform,
            languageStrings.Amount
        ].map(headerText => ({ v: headerText, s: { font: { bold: true } } }));
        XLSX.utils.sheet_add_aoa(ws, [headerRow], { origin: "A1" });

        const wscols = [
            { wch: 20 },
            { wch: 25 },
            { wch: 15 },
            { wch: 15 },
            { wch: 20 },
            { wch: 45 },
            { wch: 20 },
            { wch: 20 },
            { wch: 20 },
            { wch: 20 },
            { wch: 20 },
            { wch: 10 }
        ];
        ws["!cols"] = wscols;
        const filename = selectedTenant?.friendlyName
            ? `Docubird_${languageStrings.Subscriptions}_${selectedTenant.friendlyName}.xlsx`
            : `Docubird_${languageStrings.Subscriptions}.xlsx`;
        XLSX.writeFile(wb, filename, { compression: true });
        setIsExporting(false);
    }, [selectedTenant, subscriptions]);

    const onSearchFilter = React.useCallback((filterText: string) => {
        setFilterValue(filterText);
    }, []);
    const onChangeFilter = React.useCallback((event?: React.ChangeEvent<HTMLInputElement>, filterText?: string) => {
        setFilterValue(filterText);
    }, []);
    const onClearFilter = React.useCallback(() => {
        setFilterValue(null);
    }, []);
    const onEscapeFilter = React.useCallback(() => {
        setFilterValue(null);
    }, []);

    const revokeAll = async (items: any[]) => {
        try {
            await Promise.all(items.map(async item => {
                await revokeAllUsersFromSubscription(item.id);

                const tenantUsers: ITenantUser[] = users.filter(user => user.subscriptionId === item.id);

                for (let index = 0; index < tenantUsers.length; index++) {
                    const newTenantUser = { ...tenantUsers[index] };
                    newTenantUser.subscriptionId = null;
                    newTenantUser.licenseType = getUserLicenseType(null);
                    newTenantUser.productType = null;
                    updateUser(newTenantUser, true);
                }
            }))
        } catch {

        }

        setIsBusy(false);
        if (selectedTenant) {
            fetchSubscrptionsAndPayments();
        } else if (!selectedTenant && tenants?.length > 1 && !transactionId) {
            fetchMultiTenantSubscrptions();
        }
    }

    const assignAll = async (item: any) => {
        try {
            const request: AssignSubscriptionRequest = {
                tenantId: item.tenantId,
                subscriptionId: item.id
            }
            const updatedUserIds = await assignAllUsersToSubscription(request);

            if (Array.isArray(updatedUserIds) && updatedUserIds.length > 0) {
                const tenantUsers: ITenantUser[] = users?.filter(user => updatedUserIds.includes(user.id)) ?? [];

                for (let index = 0; index < tenantUsers.length; index++) {
                    const newTenantUser = { ...tenantUsers[index] };
                    newTenantUser.subscriptionId = item.id;
                    newTenantUser.licenseType = getUserLicenseType(item);
                    newTenantUser.productType = item.product?.trim() ?? null;
                    updateUser(newTenantUser, true);
                }
            }
        } catch (error) {
            console.log("error", error)
        }

        setIsBusy(false);
        if (selectedTenant) {
            fetchSubscrptionsAndPayments();
        } else if (!selectedTenant && tenants?.length > 1 && !transactionId) {
            fetchMultiTenantSubscrptions();
        }
    }

    const updateStatus = async (items: any[], status: SubscriptionStatusEnum) => {
        try {
            await Promise.all(items.map(async item => {
                const request: SubscriptionStatusUpdateRequest = {
                    status: status,
                    subscriptionId: item.id
                }

                await updateSubscriptionStatus(request);

                const tenantUsers: ITenantUser[] = users.filter(user => user.subscriptionId === item.id);

                const updatedSubscription = {
                    ...item,
                    status
                }

                for (let index = 0; index < tenantUsers.length; index++) {
                    const newTenantUser = { ...tenantUsers[index] };
                    newTenantUser.licenseType = getUserLicenseType(updatedSubscription);
                    updateUser(newTenantUser, true);
                }
            }))
        } catch (error) {

        }

        setIsBusy(false);
        if (selectedTenant) {
            fetchSubscrptionsAndPayments();
        } else if (!selectedTenant && tenants?.length > 1 && !transactionId) {
            fetchMultiTenantSubscrptions();
        }
    }

    const cancelSubscriptions = async (items: any[]) => {
        await Promise.all(items.map(async item => {
            await cancelSubscription(item.id);
        }))
        setIsBusy(false);
        if (selectedTenant) {
            fetchSubscrptionsAndPayments();
        } else if (!selectedTenant && tenants?.length > 1 && !transactionId) {
            fetchMultiTenantSubscrptions();
        }
    }

    return (
        <>
            {paymentStatus && <div style={{ paddingTop: 15, paddingRight: 30 }}>
                <MessageBar
                    messageBarType={paymentStatus.status === "paid" ? MessageBarType.success : MessageBarType.warning}
                    onDismiss={() => {
                        setPaymentStatus(null);
                    }}
                >
                    {languageStrings.PaymentStatus}: {paymentStatus.status}
                </MessageBar>
            </div>}

            <div style={{ position: "relative" }}>

                <Label styles={headingStyle}>{`${selectedTenant ? `${languageStrings.Subscriptions} (${selectedTenant.friendlyName})` : languageStrings.Subscriptions}`}</Label>
                <CommandBar
                    items={commandBarItems}
                    farItems={[
                        {
                            key: "Search",
                            onRender: () =>
                                <SearchBox
                                    styles={UserTableStyle.searchBoxStyles}
                                    disabled={!subscriptions?.length}
                                    placeholder={languageStrings.Search}
                                    value={filterValue ?? ""}
                                    onSearch={onSearchFilter}
                                    onChange={onChangeFilter}
                                    onClear={onClearFilter}
                                    onEscape={onEscapeFilter}
                                />,
                        }
                    ]}
                />
                {(selectedTenant || (tenants?.length > 0 && subscriptions?.length > 0)) && <div className={tableContainerStyle} id="tableContainer">
                    <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                        <Stack tokens={{ childrenGap: 70 }}>
                            {subscriptions?.length > 0 &&
                                <SubscriptionsList
                                    items={subscriptions}
                                    isLoading={isLoading}
                                    itemSelected={(items: any[]) => {
                                        setSelectedItems(items);
                                    }}
                                    deselect={deselectAllSubscriptions}
                                    isMultiTenant={!selectedTenant}
                                    filterText={filterValue}
                                />}
                            {/* {subscriptions?.length > 0 && selectedTenant && <SubscriptionsPaymentsList items={payments} isLoading={isLoading} paymentMethods={paymentMethods} />} */}
                            {subscriptions?.length === 0 &&
                                <div style={{ textAlign: "center", paddingTop: 20 }}>
                                    <Label styles={labelHeadingStyle}>
                                        {languageStrings.NoSubscriptionsFound}
                                    </Label>
                                </div>}
                        </Stack>
                    </ScrollablePane>
                </div>}

                {isAddSubscriptionPanelOpen &&
                    <AddSubscriptionPanel
                        isOpen={isAddSubscriptionPanelOpen}
                        closePanel={() => setIsAddSubscriptionPanelOpen(false)}
                        paymentMethods={paymentMethods}
                        tenant={selectedTenant}
                        onSuccess={() => {
                            fetchSubscrptionsAndPayments();
                        }}
                    />}
                {isSelectTenantPanelOpen &&
                    <SelectTenantPanel
                        isOpen={isSelectTenantPanelOpen}
                        closePanel={() => setIsSelectTenantPanelOpen(false)}
                        onTenantSelected={setSelectedTenant}
                        tenants={tenants}
                    />}
                {isExportPanelOpen &&
                    <SubscriptionsExportPanel
                        isOpen={isExportPanelOpen}
                        dismissPanel={() => setIsExportPanelOpen(false)}
                    />}
            </div>
        </>

    )
}

export default connector(Subscriptions);
